import React, { useEffect, useContext } from 'react';
import { Formik, Form } from 'formik';
import Loader from 'features/Loader/Loader';
import { connect } from 'react-redux';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import confirmDiscardChanges from 'shared/components/confirm-discard-changes/confirmDiscardChanges';
import { AuthContext } from 'features/auth/context/Auth';
import Title from 'shared/components/title/Title';
import { goalsSchema } from 'utils/validators/goals';
import GoalOverview from './GoalOverview';
import GoalNutrition from './GoalNutrition';
import GoalExercise from './GoalExercise';
import GoalStrength from './GoalStrength';
import './Goal.scss';
import { getClient } from '../../ducks/clientProfileActions';
import { getGoal, addGoal } from './ducks/goalActions';
import { getExercises } from './ducks/exerciseAction';

const GoalAdd = ({
  getClient,
  getGoal,
  getExercises,
  addGoal,
  goal,
  exercises,
  client,
  goalIsSaved,
  history,
  loadingClient,
  loadingGoal,
  loadingExercises,
  ...props
}) => {
  const clientId = props.match.params.client_id;
  const { currentUser } = useContext(AuthContext) || {};

  const build = {
    calories: goal?.nutrition?.calories ? `${goal.nutrition.calories}` : '',
    carbs: goal?.nutrition?.carbs ? `${goal.nutrition.carbs}` : '',
    fat: goal?.nutrition?.fat ? `${goal.nutrition.fat}` : '',
    protein: goal?.nutrition?.protein ? `${goal.nutrition.protein }`: '',
    water: goal?.nutrition?.water ? `${goal.nutrition.water}` : '',
    minutes: goal?.exercise?.minutes ? `${goal.exercise.minutes}` : '',
    type: goal?.performance?.type ? goal.performance.type : '',
    due_date: goal?.performance?.due_date ? goal.performance.due_date : '',
    exercise: goal?.performance?.strength?.exercise
      ? goal.performance.strength.exercise
      : '',
    exercise_name: goal?.performance?.strength?.exercise_name
      ? goal.performance.strength.exercise_name
      : '',
    max_lift: goal?.performance?.strength?.max_lift
      ? `${goal.performance.strength.max_lift}`
      : '',
    description: goal?.performance?.other?.description
      ? goal.performance.other.description
      : '',
    goal: goal?.performance?.other?.goal ? `${goal.performance.other.goal}` : '',
    goal_unit: goal?.performance?.other?.goal_unit
      ? goal.performance.other.goal_unit
      : ''
  };

  useEffect(() => {
    getClient(clientId, history);
    getGoal(clientId, history);
    getExercises();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  if (loadingClient || loadingGoal || loadingExercises) {
    return <Loader />;
  }
  return (
    <div>
      <Formik
        validateOnChange={true}
        enableReinitialize={true}
        initialValues={build}
        onSubmit={values => {
          const goal = constructGoalsObject(values, currentUser.uid);
          return addGoal(goal, clientId, history);
        }}
        validationSchema={goalsSchema}
      >
        {({
          isSubmitting,
          isValid,
          values,
          errors,
          setFieldValue,
          touched,
          setFieldTouched,
          submitCount,
          dirty
        }) => (
          <Form>
            {isSubmitting ? (
              <Loader />
            ) : (
              <div className="client-form-pages">
                <div className="container">
                  <Breadcrumb>
                    <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
                    <Breadcrumb.Item href={`/clients/${clientId}`}>
                      {`${client?.first_name}  ${client?.last_name}`}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item active>Add Goal</Breadcrumb.Item>
                  </Breadcrumb>
                  <Title
                    pageTitle="Add Goal"
                    linkText="Cancel"
                    buttonText="Save"
                    cancelLink={`/clients/${clientId}`}
                    cancelBtn={
                      dirty &&
                      (() =>
                        confirmDiscardChanges(`/clients/${clientId}`, history))
                    }
                    disabled={!dirty}
                  />

                  <div className="add-goal-page">
                    <GoalOverview
                      goal={client?.client_goal}
                      image={client?.photo}
                      clientId={clientId}
                      client={client}
                    />

                    <ul className="goals-list">
                      <li>
                        <GoalNutrition
                          goal={values}
                          setFieldValue={setFieldValue}
                          errors={errors}
                          values={values}
                          setFieldTouched={setFieldTouched}
                        />
                        <div className="input-feedback">
                          {submitCount > 0 &&
                            (errors.calories ||
                              errors.carbs ||
                              errors.fat ||
                              errors.protein ||
                              errors.water) &&
                            "Goal Nutrition's Required fields are missing!"}
                        </div>
                      </li>

                      <li>
                        <GoalExercise
                          goal={values}
                          setFieldValue={setFieldValue}
                          errors={errors}
                          values={values}
                        />
                        <div className="input-feedback">
                          {submitCount > 0 &&
                            errors.minutes &&
                            "Goal Exercise's Required field is missing!"}
                        </div>
                      </li>
                      <li>
                        <GoalStrength
                          goal={values}
                          unit={client?.measurement_unit}
                          exercises={exercises?.map(exercise => {
                            return {
                              value: exercise?.id,
                              option: exercise?.name
                            };
                          })}
                          setFieldValue={setFieldValue}
                          errors={errors}
                          values={values}
                          setFieldTouched={setFieldTouched}
                        />
                        <div className="input-feedback">
                          {submitCount > 0 &&
                            (errors.strengthMod || errors.type) &&
                            "Goal Strength's Required fields are missing!"}
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    client: state.clientProfileReducer.client,
    loadingClient: state.clientProfileReducer.loading,
    goal: state.goalReducer.goal,
    loadingGoal: state.goalReducer.loading,
    goalIsSaved: state.goalReducer.goalIsSaved,
    exercises: state.exerciseReducer.exercises,
    loadingExercises: state.exerciseReducer.loading
  };
}

function mapDispatchToProps(dispatch) {
  return {
    getClient: (clientId, history) => dispatch(getClient(clientId, history)),
    getGoal: (clientId, history) => dispatch(getGoal(clientId, history)),
    addGoal: (goal, clientId, history) =>
      dispatch(addGoal(goal, clientId, history)),
    getExercises: () => dispatch(getExercises())
  };
}

const constructGoalsObject = (values, trainerId) => {
  if (!values) return false;
  const goal = {
    trainer_id: trainerId,
    created_at: new Date(),
    exercise: {
      minutes: ~~values.minutes
    },
    nutrition: {
      calories: ~~values.calories,
      carbs: ~~values.carbs,
      fat: ~~values.fat,
      protein: ~~values.protein,
      water: ~~values.water
    },
    performance: {
      type: values.type,
      due_date: values.due_date
    }
  };
  if (values.type === 'Other') {
    goal.performance.other = {
      description: values.description,
      goal: ~~values.goal,
      goal_unit: values.goal_unit
    };
  } else if (values.type === 'Strength') {
    goal.performance.strength = {
      exercise: values.exercise,
      exercise_name: values.exercise_name,
      max_lift: ~~values.max_lift
    };
  } else {
    return false;
  }
  return goal;
};

export default connect(mapStateToProps, mapDispatchToProps)(GoalAdd);
